/*
 ╔══════════════════════════════════════════════════════════╗
 ║  CHOPTIME AI — api.js                                   ║
 ║  All API calls to the real backend in one place         ║
 ║                                                          ║
 ║  Usage:                                                  ║
 ║  import api from './api';                               ║
 ║  const result = await api.auth.login(email, password);  ║
 ║                                                          ║
 ║  In memory of Mina Gbemisola LAAK · MGL2390             ║
 ╚══════════════════════════════════════════════════════════╝
*/

const BASE_URL = "https://choptime-ai-backend.onrender.com";

// ── TOKEN HELPERS ──────────────────────────────────────────
// Store session token in localStorage
function getToken() {
  return localStorage.getItem("choptime_token");
}

function setToken(token) {
  localStorage.setItem("choptime_token", token);
}

function clearToken() {
  localStorage.removeItem("choptime_token");
  localStorage.removeItem("choptime_user");
}

function setUser(user) {
  localStorage.setItem("choptime_user", JSON.stringify(user));
}

function getUser() {
  try {
    return JSON.parse(localStorage.getItem("choptime_user"));
  } catch {
    return null;
  }
}

function isLoggedIn() {
  return !!getToken();
}

// ── CORE FETCH HELPER ──────────────────────────────────────
async function request(method, path, body = null, requiresAuth = true) {
  const headers = { "Content-Type": "application/json" };

  if (requiresAuth) {
    const token = getToken();
    if (!token) throw new Error("Not logged in. Please sign in.");
    headers["Authorization"] = `Bearer ${token}`;
  }

  const options = { method, headers };
  if (body) options.body = JSON.stringify(body);

  try {
    const response = await fetch(`${BASE_URL}${path}`, options);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Something went wrong. Please try again.");
    }

    return data;
  } catch (err) {
    if (err.message === "Failed to fetch") {
      throw new Error(
        "Cannot connect to server. Please check your internet connection."
      );
    }
    throw err;
  }
}

// ── API METHODS ────────────────────────────────────────────
const api = {
  // ── AUTH ────────────────────────────────────────────────
  auth: {
    async signup(firstName, lastName, email, password, country, phone) {
      const data = await request(
        "POST",
        "/api/auth/signup",
        {
          firstName,
          lastName,
          email,
          password,
          country,
          phone,
        },
        false
      );
      if (data.session?.access_token) {
        setToken(data.session.access_token);
        setUser(data.user);
      }
      return data;
    },

    async login(email, password) {
      const data = await request(
        "POST",
        "/api/auth/login",
        {
          email,
          password,
        },
        false
      );
      if (data.access_token) {
        setToken(data.access_token);
        setUser(data.user);
      }
      return data;
    },

    async logout() {
      try {
        await request("POST", "/api/auth/logout");
      } finally {
        clearToken();
      }
    },

    async getProfile() {
      return request("GET", "/api/auth/profile");
    },

    async updateProfile(updates) {
      return request("PUT", "/api/auth/profile", updates);
    },

    isLoggedIn,
    getUser,
    getToken,
  },

  // ── BLOOD SUGAR ─────────────────────────────────────────
  sugar: {
    async log(value, unit, timing, notes) {
      const data = await request("POST", "/api/sugar/log", {
        value,
        unit,
        timing,
        notes,
      });

      // Auto-send alert if sugar is dangerous
      if (data.alertNeeded) {
        try {
          await api.alerts.send(data.alertType, data.mmolValue);
        } catch {
          // Don't fail the log if alert fails
        }
      }

      return data;
    },

    async getLogs(days = 30) {
      return request("GET", `/api/sugar/logs?days=${days}`);
    },

    async getStats() {
      return request("GET", "/api/sugar/stats");
    },
  },

  // ── AI MEAL PLANNER ─────────────────────────────────────
  ai: {
    async mealPlan(query, country, city, lastSugarReading, budget) {
      return request("POST", "/api/ai/meal-plan", {
        query,
        country,
        city,
        lastSugarReading,
        budget,
      });
    },

    async getHistory() {
      return request("GET", "/api/ai/history");
    },
  },

  // ── NEARBY FOOD ─────────────────────────────────────────
  nearby: {
    async getFood(lat, lng, country, radius = 3000) {
      return request(
        "GET",
        `/api/nearby/food?lat=${lat}&lng=${lng}&country=${country}&radius=${radius}`
      );
    },

    // Get user's current location
    getCurrentLocation() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          // Default to Accra if no geolocation
          resolve({ lat: 5.6037, lng: -0.187, city: "Accra" });
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) =>
            resolve({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            }),
          () => resolve({ lat: 5.6037, lng: -0.187, city: "Accra" }),
          { timeout: 8000 }
        );
      });
    },
  },

  // ── ALERTS ──────────────────────────────────────────────
  alerts: {
    async send(alertType, sugarValue, message) {
      return request("POST", "/api/alerts/send", {
        alertType,
        sugarValue,
        message,
      });
    },

    async getHistory() {
      return request("GET", "/api/alerts/history");
    },
  },

  // ── EMERGENCY CONTACTS ───────────────────────────────────
  contacts: {
    async getAll() {
      return request("GET", "/api/contacts");
    },

    async add(name, phone, whatsapp, relationship, isPrimary) {
      return request("POST", "/api/contacts", {
        name,
        phone,
        whatsapp,
        relationship,
        isPrimary,
      });
    },

    async remove(id) {
      return request("DELETE", `/api/contacts/${id}`);
    },
  },

  // ── WAITLIST ─────────────────────────────────────────────
  waitlist: {
    async join(firstName, lastName, email, phone, country, challenge) {
      return request(
        "POST",
        "/api/waitlist",
        {
          firstName,
          lastName,
          email,
          phone,
          country,
          challenge,
        },
        false
      );
    },

    async getCount() {
      return request("GET", "/api/waitlist/count", null, false);
    },
  },

  // ── QUIZ ─────────────────────────────────────────────────
  quiz: {
    async submit(question, correct) {
      return request("POST", "/api/quiz/submit", { question, correct });
    },

    async getStats() {
      return request("GET", "/api/quiz/stats");
    },
  },
};

export default api;
export { getToken, getUser, isLoggedIn, setToken, setUser, clearToken };
